import { IonContent, createAnimation, isPlatform } from '@ionic/react';
import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FormInput } from '@/components/FormInput';
import { amplitudeModule } from '@/modules/amplitude';
import { OnboardingFooter } from '@/pages/Onboarding/components/Footer';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { OnboardingPage } from '@/pages/Onboarding/components/Page';
import { OnboardingTitle } from '@/pages/Onboarding/components/Title';
import { MAX_USERNAME_LENGTH } from '@/pages/Onboarding/constants';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OnboardingParamsTool } from '@/pages/Onboarding/utils/paramsTool';
import { canLogEvents } from '@/utils/canLogEvents';

import { LayoutDataType } from '../../types';

import { animationKeyFrames } from './constants';
import './Name.scss';

import type { Animation } from '@ionic/react';

export const ScreenName: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Name',
      });
    }
  }, []);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const [showInput, setShowinput] = useState<boolean>(true);
  const [userName, setUserName] = useState<string>('');

  const inputRef = useRef<HTMLIonInputElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const nameResponseRef = useRef<HTMLHeadingElement | null>(null);
  const responseRef = useRef<HTMLHeadingElement | null>(null);
  const titleAnimRef = useRef<Animation>();
  const responseAnimRef = useRef<Animation>();

  const formName = form.watch('name');

  const disableNextStep = !formName;

  const play = async (): Promise<void> => {
    await titleAnimRef.current?.play();
    await responseAnimRef.current?.play();
  };

  const submit = (): void => {
    if (formName) {
      setShowinput(false);
      setUserName(formName);
      OnboardingParamsTool.setParam('name', formName);
      play();
    }
  };

  React.useEffect(() => {
    setUserName('');

    if (isPlatform('android') || isPlatform('desktop')) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.getInputElement().then((inputEl) => {
            inputEl.focus();
          });
        }
      });

      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.getInputElement().then((inputEl) => {
            inputEl.focus();
          });
        }
      }, 1000);
    }
  }, []);

  React.useEffect(() => {
    if (!titleAnimRef.current) {
      const titleA = createAnimation()
        .addElement(titleRef.current!)
        .keyframes(animationKeyFrames.title)
        .afterAddClass('transparent-with-shadow');

      const nameA = createAnimation()
        .addElement(nameResponseRef.current!)
        .keyframes(animationKeyFrames.name)
        .afterAddClass('transparent-with-shadow');

      const repsonse = createAnimation()
        .addElement(responseRef.current!)
        .fill('none')
        .duration(1500)
        .easing('linear')
        .keyframes(animationKeyFrames.response)
        .afterAddClass('color-without-shadow');

      titleAnimRef.current = createAnimation()
        .duration(2000)
        .addAnimation([titleA, nameA, repsonse]);
    }
  }, []);

  const handleNextStep = (): void => {
    wizard.nextStep();
  };

  const handleBackStep = () => {
    wizard.goToStep('topics');
  };

  return (
    <OnboardingPage className="onboarding-name">
      <OnboardingNavigation onBack={handleBackStep} />

      <div className="response">
        <OnboardingTitle ref={titleRef} color="black">
          What would you like AI characters to call you?
        </OnboardingTitle>
        <OnboardingTitle ref={nameResponseRef} color="black">
          {userName}
        </OnboardingTitle>
        <OnboardingTitle
          ref={responseRef}
          className="response-start"
          color="black"
        >
          Nice to meet you, {formName}!
        </OnboardingTitle>
      </div>

      <IonContent>
        <FormInput
          form={form}
          hidden={!showInput}
          disableButton={disableNextStep}
          onSubmit={submit}
          ref={inputRef}
          name="name"
          placeholder="Type your name"
          type="text"
          className="onboarding-name__input"
          maxLength={MAX_USERNAME_LENGTH}
        />
      </IonContent>

      <OnboardingFooter
        disabled={disableNextStep}
        onContinue={handleNextStep}
        hidden={showInput}
      />
    </OnboardingPage>
  );
};
